import { useState, type ReactNode } from 'react'

import type { VarlessCopyId } from '@npc/copy'

import { T } from '../../copy/text'
import {
  mayUserPick,
  reasonTextFor,
  toneForStatus,
  type ModelOption,
  type ModelStatus,
} from './model-picker'
import { SubFailExits } from './sidecar-panels'
import { needsUserDecision, summarizeSubFailure, type SubFailExitKind } from './subfail'
import {
  canDispatch,
  resolveDisplayName,
  teamSize,
  type HumanMember,
  type SubAgentRun,
  type TeamMember,
} from './roster'
import type { CurrentPlan, RosterMember } from './wire'
import {
  ITEM_STATE_FACTS,
  progressBarPercent,
  projectItemState,
  type ItemState,
  type LinkState,
  type Tone,
} from './session-state'

/**
 * Sidecar 里四个页签的内容。
 *
 * # 这几个组件里最要紧的是「层级」，不是「样式」
 *
 * 设计稿把成员卡的三层顺序写成了硬规则：
 *
 *   [头像] 小柯                      [执行中]
 *          前端 · 正在抽结算页文案
 *          claude-sonnet-4.5              ← 第三层，灰色小字
 *
 * 原话：「模型 ID 从第一层降到第三层——之前它和名字并排，
 * 读起来像『这个成员叫 claude-sonnet-4.5』」。
 *
 * 所以模型那一行在这里永远是最后一个、永远用 `--t4`、永远等宽小字。
 * 这三样任何一样变了，那句「Agent 是具名角色，模型只是它的配置」
 * 在界面上就不成立了。
 */

const TONE_TEXT: Record<Tone, string> = {
  n: 'text-npc-lulu-t2',
  g: 'text-npc-lulu-ok',
  w: 'text-npc-lulu-warn',
  r: 'text-npc-lulu-bad',
  a: 'text-npc-lulu-t1',
}

const TONE_PILL: Record<Tone, string> = {
  n: 'bg-npc-lulu-bg-3 text-npc-lulu-t3',
  g: 'bg-npc-lulu-ok/15 text-npc-lulu-ok',
  w: 'bg-npc-lulu-warn/15 text-npc-lulu-warn',
  r: 'bg-npc-lulu-bad/15 text-npc-lulu-bad',
  a: 'bg-npc-lulu-bg-4 text-npc-lulu-t1',
}

const TONE_BAR: Record<Tone, string> = {
  n: 'bg-npc-lulu-t3',
  g: 'bg-npc-lulu-ok',
  w: 'bg-npc-lulu-warn',
  r: 'bg-npc-lulu-bad',
  a: 'bg-npc-lulu-t2',
}

function StatePill({
  state,
  link,
  presence = 'live',
}: {
  state: ItemState | null
  link: LinkState
  presence?: 'live' | 'unknown'
}) {
  // presence 未知时「进行中」那几档折成「不知道」——
  // PRD 验收 #9「任一未知状态不得显示 running」。
  // 上一版只挂了个「不知道它在不在」的角标，而药丸还写着「执行中」，
  // 两句话在同一行里打架，而 PRD 要的是后者别说话。
  const projected = projectItemState(state, link, presence)
  const facts = ITEM_STATE_FACTS[projected]
  return (
    <span
      data-testid="lulu-state-pill"
      data-state={projected}
      className={[
        'shrink-0 rounded-npc-chip px-1.5 py-0.5 text-npc-meta',
        TONE_PILL[facts.tone],
      ].join(' ')}
    >
      <T id={facts.copyId} />
    </span>
  )
}

/**
 * 3px 的进度条。**没有数字就整条不渲染**——不是渲染一条 0%。
 *
 * 这个组件刻意没有「宽度为 0」这条路径：一条 0% 的条和一条真的停在 0% 的条
 * 在屏幕上是同一个东西，而它们说的是完全不同的两件事。
 */
function ProgressBar({
  progress,
  state,
  link,
  tone,
}: {
  progress: number | null
  state: ItemState
  link: LinkState
  tone: Tone
}) {
  const pct = progressBarPercent(progress, state, link)
  if (pct === null) return null
  return (
    <div
      data-testid="lulu-progress"
      data-pct={pct}
      className="mt-1 h-[3px] w-full overflow-hidden rounded-full bg-npc-lulu-bg-3" /* npc-token-allow: 设计稿进度条高 3px，整站 token 无此档 */
    >
      <div className={['h-full', TONE_BAR[tone]].join(' ')} style={{ width: `${pct}%` }} />
    </div>
  )
}

/** 名字 → 角色/在做什么 → 模型。**顺序不可颠倒。** */
function ThreeLayerRow({
  name,
  second,
  modelId,
  modelStatus,
  modelStatusReason,
  autoNamed,
  state,
  link,
  indent,
  testid,
  presence,
  children,
}: {
  name: string | null
  second: string | null
  modelId: string | null
  modelStatus?: ModelStatus | null
  modelStatusReason?: string | null
  autoNamed: boolean
  state: ItemState | null
  link: LinkState
  indent?: boolean
  testid: string
  presence?: 'live' | 'unknown'
  children?: React.ReactNode
}) {
  return (
    <li
      data-testid={testid}
      data-indent={indent ? 'yes' : 'no'}
      className={[
        'rounded-npc-chip border border-npc-lulu-line-1 bg-npc-lulu-bg-2 px-2.5 py-2',
        // 子 Agent 缩进在主任务下方。缩进量写在这里而不是靠 padding 叠加：
        // 叠加会让第三层缩得看不出层级。
        indent ? 'ml-[14px]' : '', /* npc-token-allow: 设计稿子项缩进 14px，整站 token 无此档 */
      ].join(' ')}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {/* 第一层：名字。解不出来就不显示——不拿模型名顶，也不写「未命名」。 */}
          {name ? (
            <div className="flex items-center gap-1.5">
              <span
                data-testid="lulu-row-name"
                className="min-w-0 truncate text-npc-caption font-medium text-npc-lulu-t1"
              >
                {name}
              </span>
              {autoNamed ? (
                <span
                  data-testid="lulu-row-auto-named"
                  className="shrink-0 rounded-npc-chip bg-npc-lulu-bg-3 px-1.5 py-0.5 text-npc-meta text-npc-lulu-t3"
                >
                  <T id="lulu_workbench.sub.auto_named" />
                </span>
              ) : null}
            </div>
          ) : null}

          {/* 第二层：角色和它现在在做什么。 */}
          {second ? (
            <p data-testid="lulu-row-second" className="mt-0.5 text-npc-meta text-npc-lulu-t3">
              {second}
            </p>
          ) : null}

          {/* 第三层：模型。等宽、更小、更灰，永远排在最后。
              模型状态标跟在它后面——**不可用要标出来但不隐藏**（验收 #31），
              未验证标黄且说明不自动派重要活（#32）。 */}
          {modelId || modelStatus ? (
            <p
              data-testid="lulu-row-model"
              className="mt-0.5 truncate font-npc-mono text-npc-meta text-npc-lulu-t4"
            >
              {modelId}
              {modelStatus ? (
                <ModelStatusTag status={modelStatus} reason={modelStatusReason ?? ''} />
              ) : null}
            </p>
          ) : null}
        </div>
        <StatePill state={state} link={link} presence={presence} />
      </div>
      {children}
    </li>
  )
}

/**
 * 一颗会自己管「正在做 / 做失败了」的按钮。
 *
 * **失败时不改名单**：叫失败了还把他画进在场名单，他会以为叫到了
 * （与 composer 那颗「叫进来」同一条规矩）。名单永远等服务端那份，
 * 这里只负责说清这一次成没成。
 */
function PersonAction({
  testid,
  label,
  busyLabel,
  failedLabel,
  onRun,
}: {
  readonly testid: string
  readonly label: ReactNode
  readonly busyLabel: ReactNode
  readonly failedLabel: ReactNode
  readonly onRun: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  return (
    <span className="shrink-0">
      <button
        type="button"
        data-testid={testid}
        disabled={busy}
        onClick={() => {
          setFailed(false)
          setBusy(true)
          void onRun()
            .catch(() => setFailed(true))
            .finally(() => setBusy(false))
        }}
        className="rounded-npc-chip-inline border border-npc-lulu-line-1 px-2 py-0.5 text-npc-meta text-npc-lulu-t2 disabled:opacity-60"
      >
        {busy ? busyLabel : label}
      </button>
      {failed ? (
        <span
          data-testid={testid + '-failed'}
          className="ml-1 text-npc-meta text-npc-lulu-t4"
        >
          {failedLabel}
        </span>
      ) : null}
    </span>
  )
}

/** 「一起管」里的一位，还没进这条会话。 */
export interface InvitablePerson {
  readonly userId: string
  /** 界面上叫他什么。服务端给的是 name，空着用 email。 */
  readonly label: string
  readonly role: HumanMember['role']
}

/**
 * 角色 → 文案 id。**认不出来时给一句实话**，不冒充成某一档。
 *
 * 默认成 viewer 会让老板看起来点不动，默认成 owner 更糟；
 * 两者都比「认不出他这一档」难查得多。
 */
function peopleRoleCopyId(role: HumanMember['role']) {
  switch (role) {
    case 'owner':
      return 'lulu_workbench.people.role.owner' as const
    case 'operator':
      return 'lulu_workbench.people.role.operator' as const
    case 'viewer':
      return 'lulu_workbench.people.role.viewer' as const
    default:
      return 'lulu_workbench.people.role.unknown' as const
  }
}

export interface TeamPanelProps {
  /** 队里的人。**来自 workboard 的 roster**，不是 Agent 配置库。 */
  readonly members: readonly RosterMember[]
  readonly link: LinkState
  /** 会话有没有绑项目。没绑就只列账户级 Agent，并把原因说出来。 */
  readonly bound: boolean
  /**
   * 库里还没进这条会话的那几位（设计稿 teamEmpty 的「先加一位试试」）。
   *
   * 与 `members` 分开传，**不是一个列表加个标志位**：这两段在屏上说的
   * 是两件事——上面那段是「谁在干活」，下面那段是「还能叫谁」。
   * 合成一个列表的那天，一个候选会被当成团队成员算进人数里。
   */
  readonly candidates?: readonly TeamMember[]
  /**
   * 一起管这条会话的真人。
   *
   * **与 members 分开传**，理由和 candidates 那条一样，只是更硬：
   * 人和 Agent 在屏上说的是两件事，而且 @ 他们的后果完全不同——
   * @ 一位 AI 可能开一件活，@ 一个人只是叫他来看。
   * 合成一个列表的那天，`teamSize` 会把人数进去，而那个签名正是
   * 「子 Agent 不许被数进来」那道门。
   */
  readonly people?: readonly HumanMember[]
  /**
   * 「一起管」里还没进这条会话的人。**与 people 分开传**，理由与
   * candidates 那条一样：上面那段是「谁在这条里」，下面那段是「还能叫谁」。
   * 合成一个的那天，一个候选会被当成在场的人算进去。
   */
  readonly invitable?: readonly InvitablePerson[]
  /** 叫一个人进来。**没有这个回调 = 这个人没有 manage_team**，那一段整个不出现。 */
  readonly onInvitePerson?: (userId: string) => Promise<void>
  /** 把一个人移出这条会话。没有 = 不给这个动作。 */
  readonly onRemovePerson?: (userId: string) => Promise<void>
  /**
   * 把一位候选加进这条会话（画板 teamEmpty 每行的 `actions:[['加入团队','p']]`）。
   *
   * **不给就不渲染那颗按钮**——没有落点的按钮比没有按钮更糟：
   * 它把「这里能加人」讲了一遍又不让人加。
   */
  readonly onJoinTeam?: (agentProfileId: string) => Promise<void>
  /**
   * 当前那一版计划。`undefined` = 还在读，`null` = 真的还没有。
   * 两者不许合并：还在读时什么都不说，真的没有时要说「还没有计划」。
   */
  readonly plan?: CurrentPlan | null
}

/**
 * 一位成员的模型状态标。
 *
 * **七档来自服务端，染色规则来自 `toneForStatus`，原因优先用服务端原话。**
 * 这三件事都不在这里判——组件只管画，判断在纯函数里，因为每一条
 * 都对应一句验收，而验收要的是「违反它会红」。
 */
function ModelStatusTag({ status, reason }: { status: ModelStatus; reason: string }) {
  if (status === 'ok') return null
  const tone = toneForStatus(status)
  const text = reasonTextFor({ id: '', name: '', status, reason })
  return (
    <span
      data-testid="lulu-model-status"
      data-status={status}
      data-tone={tone}
      data-pickable={mayUserPick({ id: '', name: '', status, reason }) ? 'yes' : 'no'}
      className={[
        'ml-1 rounded-npc-chip-inline px-1 text-npc-meta',
        tone === 'bad' ? 'text-npc-lulu-bad' : 'text-npc-lulu-warn',
      ].join(' ')}
    >
      {text ?? <T id="lulu_workbench.model.unverified" />}
    </span>
  )
}

/**
 * 候选行上的「加入团队」。
 *
 * 三件事值得说清：
 *
 * 1. **断线时不可点。** 这一动作要发请求，而断线时点下去只会静默失败。
 *    设计稿把断线画成「你说的话会存本机草稿」——那是**能存**的东西；
 *    加人存不了本机，所以这里是禁用而不是排队。
 * 2. **失败不清状态。** 回到可点，并说一句「再点一下就行」——
 *    用户的下一个动作是重试，那句话就得让他知道重试有用。
 * 3. **进行中禁用自己。** 同一位连点两次会在会话里留下两条参与方记录，
 *    而用户只想加一次。
 */
function JoinButton({
  id,
  onJoin,
  link,
}: {
  id: string
  onJoin: (agentProfileId: string) => Promise<void>
  link: LinkState
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'failed'>('idle')
  // LinkState 只有 'live' | 'down' 两档。
  const offline = link === 'down'
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1.5">
      {state === 'failed' ? (
        <span data-testid="lulu-team-join-failed" className="text-npc-meta text-npc-lulu-warn">
          <T id="lulu_workbench.team.join_failed" />
        </span>
      ) : null}
      <button
        type="button"
        data-testid="lulu-team-join"
        data-agent={id}
        disabled={state === 'busy' || offline}
        onClick={() => {
          setState('busy')
          void onJoin(id).then(
            () => setState('idle'),
            () => setState('failed'),
          )
        }}
        className="rounded-npc-chip-inline bg-npc-lulu-t1 px-2 py-0.5 text-npc-meta font-medium text-npc-lulu-bg-0 disabled:opacity-40"
      >
        <T id={state === 'busy' ? 'lulu_workbench.team.joining' : 'lulu_workbench.team.join'} />
      </button>
    </span>
  )
}

/**
 * 成员行上的两件事实：在不在线、是不是已经停用。
 *
 * **presence 与 state 分开显示，不合并成一个词。** 交接 §1.3 把代价说清了：
 * state 判错会打断一个正在干活的 Agent，presence 判错只是多显示一次
 * 「不知道」。合成一句的那天，一次长模型调用挡住心跳，界面就会说
 * 「它失败了」——而它只是没吭声。
 *
 * unknown 那一档只写「不知道它在不在」**不画任何进度**——
 * 那条规则在设计包里有四处出处：SPEC-subagent-run-panel-v3.md:63
 * 「没有 heartbeat/事件时显示"状态未知"，停止动画，不画进度条」、
 * README.md:143、LuluV3.dc.html:2599「我没有它的进度，画一个就是编的」、
 * 多Agent协作.dc.html:557。
 *
 * 停用的成员照常列出并标出来：他当时确实在队里，抹掉等于改写历史。
 */
function MemberFacts({ member }: { member: RosterMember }) {
  // **一个事实一个闸门，没有外层的提前 return。**
  //
  // 上一版有一句 `if (live && !disabled) return null` 当快捷方式，
  // 而它让里面两个条件在「在线且没停用」这条路上**根本走不到**——
  // 于是把里面任何一个写成无条件渲染，用例照样全绿。
  // 变异验证抓到的就是这个：两处变异都落地了、都没红。
  //
  // 现在两个条件各自是唯一的闸门，改坏任何一个都会有用例红。
  // 代价是两个都不用说时多渲染一个空 div——那点代价换的是可测性。
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {member.presence === 'unknown' ? (
        <span
          data-testid="lulu-member-presence"
          data-presence="unknown"
          className="rounded-npc-chip-inline bg-npc-lulu-warn/15 px-1.5 text-npc-meta text-npc-lulu-warn"
        >
          <T id="lulu_workbench.presence.unknown" />
        </span>
      ) : null}
      {member.disabled ? (
        <span
          data-testid="lulu-member-disabled"
          className="rounded-npc-chip-inline bg-npc-lulu-bg-3 px-1.5 text-npc-meta text-npc-lulu-t3"
        >
          <T id="lulu_workbench.team.member_disabled" />
        </span>
      ) : null}
    </div>
  )
}

export function TeamPanel({
  members,
  link,
  bound,
  candidates,
  people,
  invitable,
  onInvitePerson,
  onRemovePerson,
  onJoinTeam,
  plan,
}: TeamPanelProps) {
  const size = teamSize(members)
  return (
    <div data-testid="lulu-panel-team">
      {/*
        当前那一版计划。**放在团队这一栏而不是工作板**：它回答的是
        「这一版是按谁排的」——那是关于**人**的问题，而队里的人就列在下面。

        三态分开：undefined 还在读（什么都不说）／null 真的还没有计划／
        有计划时说得出第几稿、按几位排的、以及现在队里几位。
        「一致」与「不一致」是两句话不是一句：一致时用户什么都不用做，
        不一致时他要判断要不要让 Lulu 按新的人重排一版。
      */}
      {plan === undefined ? null : plan === null ? (
        <p data-testid="lulu-team-no-plan" className="mb-2 text-npc-meta text-npc-lulu-t4">
          <T id="lulu_workbench.plan.none" />
        </p>
      ) : (
        <div data-testid="lulu-team-plan" data-revision={plan.revision} className="mb-2">
          <p className="text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.plan.current" vars={{ revision: String(plan.revision) }} />
          </p>
          <p
            data-testid="lulu-team-plan-roster"
            data-changed={plan.plannedFor === plan.rosterNow ? 'no' : 'yes'}
            className={[
              'mt-0.5 text-npc-meta',
              plan.plannedFor === plan.rosterNow ? 'text-npc-lulu-t4' : 'text-npc-lulu-warn',
            ].join(' ')}
          >
            {plan.plannedFor === plan.rosterNow ? (
              <T id="lulu_workbench.plan.same_roster" vars={{ count: String(plan.rosterNow) }} />
            ) : (
              <T
                id="lulu_workbench.plan.roster_changed"
                vars={{ planned: String(plan.plannedFor), now: String(plan.rosterNow) }}
              />
            )}
          </p>
        </div>
      )}

      {size === 0 ? (
        <div data-testid="lulu-team-empty">
          {/* 空团队不是错误状态。设计稿 Z06 第一句就是这个。 */}
          <p className="text-npc-caption text-npc-lulu-t2">
            <T id="lulu_workbench.team.empty_ok" />
          </p>
        </div>
      ) : (
        <ul className="grid gap-1.5">
          {members.map((member) => (
            <ThreeLayerRow
              key={member.id}
              testid="lulu-team-member"
              name={resolveDisplayName(member)}
              second={member.role}
              modelId={member.modelId}
              autoNamed={member.nameSource === 'auto'}
              state={member.state}
              link={link}
              presence={member.presence}
            >
              <MemberFacts member={member} />
            </ThreeLayerRow>
          ))}
        </ul>
      )}

      {/* 「先加一位试试」。空团队那句话说清了状态，这一段给下一步。 */}
      {(candidates ?? []).length === 0 ? null : (
        <div data-testid="lulu-team-candidates" className="mt-3">
          <p className="text-npc-caption text-npc-lulu-t2">
            <T id="lulu_workbench.team.try_one" />
          </p>
          <p className="mt-0.5 text-npc-meta text-npc-lulu-t4">
            <T
              id="lulu_workbench.team.candidate_count"
              vars={{ count: String((candidates ?? []).length) }}
            />
          </p>
          <ul className="mt-1.5 grid gap-1.5">
            {(candidates ?? []).map((member) => (
              <ThreeLayerRow
                key={member.id}
                testid="lulu-team-candidate"
                name={resolveDisplayName(member)}
                second={member.role}
                modelId={member.modelId}
                autoNamed={member.nameSource === 'auto'}
                state={null}
                link={link}
              >
                {onJoinTeam === undefined ? null : (
                  <JoinButton id={member.id} onJoin={onJoinTeam} link={link} />
                )}
              </ThreeLayerRow>
            ))}
          </ul>
        </div>
      )}

      {/*
        一起管这条的人。**排在 Agent 之后、说明之前**：这一栏的主语是
        「谁在干活」，人是来一起管的，不是来干活的。
      */}
      <div data-testid="lulu-team-people" className="mt-3">
        <p className="text-npc-caption text-npc-lulu-t2">
          <T id="lulu_workbench.people.title" />
        </p>
        {(people ?? []).length === 0 ? (
          <p data-testid="lulu-team-people-none" className="mt-0.5 text-npc-meta text-npc-lulu-t4">
            <T id="lulu_workbench.people.none" />
          </p>
        ) : (
          <ul className="mt-1.5 grid gap-1.5">
            {(people ?? []).map((person) => (
              <li
                key={person.id}
                data-testid="lulu-team-person"
                data-role={person.role ?? 'unknown'}
                data-can-dispatch={canDispatch(person) ? 'yes' : 'no'}
                className="rounded-npc-inset border border-npc-lulu-line-1 px-2.5 py-1.5"
              >
                <span className="block text-npc-card-title text-npc-lulu-t1">{person.name}</span>
                <span className="mt-0.5 block text-npc-meta text-npc-lulu-t3">
                  <T id={peopleRoleCopyId(person.role)} />
                </span>
                {/*
                  不能让 NPC 动手的那一档，**当场说清楚**。
                  不说的话他会以为 @ 不动是坏了——而它是设计。
                */}
                {canDispatch(person) ? null : (
                  <span
                    data-testid="lulu-team-person-readonly"
                    className="mt-0.5 block text-npc-meta text-npc-lulu-t4"
                  >
                    <T id="lulu_workbench.people.cannot_dispatch" />
                  </span>
                )}
                {onRemovePerson === undefined ? null : (
                  <PersonAction
                    testid="lulu-team-person-remove"
                    label={<T id="lulu_workbench.people.remove" />}
                    busyLabel={<T id="lulu_workbench.people.removing" />}
                    failedLabel={<T id="lulu_workbench.people.remove_failed" />}
                    onRun={() => onRemovePerson(person.id)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
        {/*
          叫一个人进来。**只列「一起管」里已有的成员**——要请全新的人
          去那一屏办（邀请是账户级动作，塞进聊天侧栏等于把红线动作
          藏进轻量面板）。

          onInvitePerson 为 undefined = 这个人没有 manage_team，
          整段不出现。**不画一颗点不动的灰按钮**：那会让他一直找
          「为什么点不动」。
        */}
        {onInvitePerson === undefined ? null : (
          <div data-testid="lulu-team-invite" className="mt-2">
            <p className="text-npc-caption text-npc-lulu-t2">
              <T id="lulu_workbench.people.invite" />
            </p>
            {(invitable ?? []).length === 0 ? (
              <p
                data-testid="lulu-team-invite-none"
                className="mt-0.5 text-npc-meta text-npc-lulu-t4"
              >
                <T id="lulu_workbench.people.invite_none" />
              </p>
            ) : (
              <ul className="mt-1 grid gap-1">
                {(invitable ?? []).map((who) => (
                  <li
                    key={who.userId}
                    data-testid="lulu-team-invitable"
                    data-user-id={who.userId}
                    className="flex items-center justify-between gap-2 rounded-npc-inset border border-npc-lulu-line-1 px-2.5 py-1.5"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-npc-card-title text-npc-lulu-t1">
                        {who.label}
                      </span>
                      <span className="block text-npc-meta text-npc-lulu-t3">
                        <T id={peopleRoleCopyId(who.role)} />
                      </span>
                    </span>
                    <PersonAction
                      testid="lulu-team-invite-do"
                      label={<T id="lulu_workbench.team.join" />}
                      busyLabel={<T id="lulu_workbench.people.inviting" />}
                      failedLabel={<T id="lulu_workbench.people.invite_failed" />}
                      onRun={() => onInvitePerson(who.userId)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <p className="mt-1 text-npc-meta text-npc-lulu-t4">
          <T id="lulu_workbench.people.add_hint" />
        </p>
      </div>

      <div className="mt-2.5 grid gap-1">
        {size > 0 ? (
          <Note id="lulu_workbench.team.remove_no_interrupt" />
        ) : null}
        {bound ? null : <Note id="lulu_workbench.team.account_only" />}
        <Note id="lulu_workbench.team.only_agents" />
      </div>
    </div>
  )
}

export interface WorkItemNode {
  readonly id: string
  readonly name: string
  readonly ownerName: string | null
  readonly modelId: string | null
  readonly state: ItemState | null
  readonly progress: number | null
  /**
   * 这一步为什么卡住，**后端渲染好的人话**；不卡住就是 null。
   *
   * 后端给的（`blocked_reason_text`），不是前端拼的——库里存的是诊断串
   * （`no_eligible_executor: kind=discussion`），而 kind 是实现层的词，
   * 不进第一屏。前端在这里**只显示，不解释**。
   *
   * 它与状态标签上那句「已确认阻塞原因，Lulu 正在处理」不是一回事：
   * 那句的前提是 Lulu 在处理，而这一档恰恰是没有人能处理。
   */
  readonly blockedReason: string | null
  /**
   * 这一步背后那条开发任务；没有就是 null。
   *
   * 撤回要用它——后端撤的是 DevelopmentTask（POST /development-tasks/{id}/cancel），
   * 不是工作项：工作项是「看得见的那一行」，任务才是「真的有人在做的那件事」。
   * 只有 kind=development 的那一步有它（000110 那条 task_binding CHECK）。
   */
  readonly taskId: string | null
  /** 挂在这一项下面的临时子 Agent。它们缩进渲染，且不计入团队人数。 */
  readonly subRuns: readonly SubAgentRun[]
}

export interface BoardPanelProps {
  readonly items: readonly WorkItemNode[]
  readonly link: LinkState
  /** 团队真实人数。底部那句边界声明要用它。 */
  readonly teamCount: number
  /**
   * 子 Agent 失败之后的补救。不给的话三颗按钮点不动——
   * **不假装点了有用**。
   */
  readonly onRecoverSubrun?: (
    subrunId: string,
    kind: SubFailExitKind,
    adapter?: string,
  ) => Promise<void>
  /** 换模型的候选。**当前那个由 SubFailExits 自己排掉。** */
  readonly swapCandidates?: readonly ModelOption[] | null
}

export function BoardPanel({
  items,
  link,
  teamCount,
  onRecoverSubrun,
  swapCandidates,
}: BoardPanelProps) {
  return (
    <div data-testid="lulu-panel-board">
      <ul className="grid gap-1.5">
        {items.map((item) => (
          <li key={item.id}>
            <ul className="grid gap-1.5">
              <ThreeLayerRow
                testid="lulu-work-item"
                name={item.name}
                second={item.ownerName}
                modelId={item.modelId}
                autoNamed={false}
                state={item.state}
                link={link}
              >
                <ProgressBar
                  progress={item.progress}
                  state={projectItemState(item.state, link)}
                  link={link}
                  tone={ITEM_STATE_FACTS[projectItemState(item.state, link)].tone}
                />
              </ThreeLayerRow>

              {/* 部分失败时，父 Agent 给三个出口（S04）。
                  **只在真的有失败时出**——`needsUserDecision` 的判据只有
                  「有失败」一条，没有「失败得够多」，因为一个会自己判断
                  「这次不用问」的分支就是一条能被造出来的旁路。 */}
              {needsUserDecision(summarizeSubFailure(item.subRuns)) ? (
                <li data-testid="lulu-subfail" className="pl-6">
                  <p className="text-npc-meta text-npc-lulu-t3">
                    <T
                      id="lulu_workbench.subfail.headline"
                      vars={{
                        done: String(summarizeSubFailure(item.subRuns).done),
                        failed: String(summarizeSubFailure(item.subRuns).failed),
                      }}
                    />
                  </p>
                  <p className="text-npc-meta text-npc-lulu-t4">
                    {/* 原来这里是「不是它的错 —— 构建机的问题」。
                        **那句需要一个我们拿不到的事实**：schema 里没有
                        failure_reason 也没有 failure_class，界面看得见的
                        只有 state='failed'。从「失败了」推出「是平台的错」，
                        用户据此就不会去看自己的代码。 */}
                    <T id="lulu_workbench.subfail.no_reason_yet" />
                  </p>
                  <p className="text-npc-meta text-npc-lulu-t4">
                    <T id="lulu_workbench.subfail.keep_done" />
                  </p>
                  <SubFailExits
                    subrunId={summarizeSubFailure(item.subRuns).failedIds[0]}
                    currentAdapter={
                      item.subRuns.find(
                        (r) => r.id === summarizeSubFailure(item.subRuns).failedIds[0],
                      )?.modelId ?? null
                    }
                    alternatives={swapCandidates}
                    onRecover={
                      onRecoverSubrun
                        ? async (kind, adapter) => {
                            // 补救的对象是**失败的那一个**，不是整项。
                            // 多个失败时取第一个：三条出路都作用在
                            // 单个执行单元上，批量补救是另一件事。
                            const id = summarizeSubFailure(item.subRuns).failedIds[0]
                            if (id) await onRecoverSubrun(id, kind, adapter)
                          }
                        : undefined
                    }
                  />
                  <p className="text-npc-meta text-npc-lulu-t4">
                    <T id="lulu_workbench.subfail.my_pick" />
                  </p>
                </li>
              ) : null}

              {/* 子 Agent 缩进在主任务下面。它们在**这个列表**里，
                  而不是在团队那一页里——位置本身就是那条边界。 */}
              {item.subRuns.map((run) => {
                const projected = projectItemState(run.state, link)
                return (
                  <ThreeLayerRow
                    key={run.id}
                    testid="lulu-sub-run"
                    indent
                    name={resolveDisplayName(run)}
                    second={run.responsibility}
                    modelId={run.modelId}
                    autoNamed={run.nameSource === 'auto'}
                    state={run.state}
                    link={link}
                  >
                    <ProgressBar
                      progress={run.progress}
                      state={projected}
                      link={link}
                      tone={ITEM_STATE_FACTS[projected].tone}
                    />
                    {/* 失联那一行要说清「为什么这里没有条」，
                        否则用户只会以为界面漏画了。 */}
                    {projected === 'unknown' ? (
                      <p
                        data-testid="lulu-sub-no-bar-note"
                        className="mt-1 text-npc-meta text-npc-lulu-warn"
                      >
                        <T id="lulu_workbench.sub.no_progress_bar" />
                      </p>
                    ) : null}
                  </ThreeLayerRow>
                )
              })}
            </ul>
          </li>
        ))}
      </ul>

      <div className="mt-2.5 grid gap-1">
        {/* 工作板是最容易把子 Agent 看成成员的地方，所以这句话固定钉在底部。 */}
        <Note id="lulu_workbench.sub.not_member" />
        <Note id="lulu_workbench.board.queue_reason" />
      </div>
      <p data-testid="lulu-board-team-count" className="mt-1 text-npc-meta text-npc-lulu-t3">
        <T id="lulu_workbench.team.mention_scope" vars={{ count: teamCount }} />
      </p>
    </div>
  )
}

function Note({ id }: { id: VarlessCopyId }) {
  return (
    <p className="text-npc-meta text-npc-lulu-t3">
      <T id={id} />
    </p>
  )
}

export { TONE_TEXT }
